/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Component for table inner filter
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */

import get from 'lodash/get';
import omit from 'lodash/omit';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { IRootReducer } from '@/main/reducers/rootReducer';
import { IMultiSelectItem } from '@components/common/MultiSelect/components/MultiSelectItemList';
import LabelSelectList from '@components/common/MultiSelect/components/SelectItemContainer/LabelSelectList';
import { IMultiSelectLabelProps } from '@components/common/MultiSelect/MultiSelect';

import { sendFieldSuggest, sendTableSearch, setTableFilter, setTableSortAction } from '../../actions/tableActions';
import isFilterEmpty from '../../helpers/isFilterEmpty';
import { ITableReducer } from '../../reducers/tableReducer';
import {
    makeSelectorTableCurrentName,
    makeSelectorTableFilter,
    makeSelectorTableFilterOptions,
    makeSelectorTableFilterSearchResult,
    makeSelectorTableFilterSearchValue,
    makeSelectorTableMetaFacets,
    makeSelectorTableSort,
    makeSelectorTableSuggestIsLoading,
} from '../../selectors/tableSelectors';
import { FilterStubStyled } from '../../styled/TableStyled';
import { ColumnFieldType, ColumnTranslationType, EColumnCell, EColumnFilter } from '../../types/columnTypes';
import {
    ISendFieldSuggestPayload,
    ISendTableSearchPayload,
    ISetTableFilterPayload,
    ISetTableSortPayload,
    TableNameType,
} from '../../types/tableTypes';
import { IColumn } from '../TableColumns/Columns';
import { IOnFilterChangeParams } from './Filter';
import FilterDate from './FilterDate';
import FilterMultiSelect from './FilterMultiSelect';
import FilterRange from './FilterRange';
import FilterSearchInput from './FilterSearchInput';
import FilterSelect from './FilterSelect';
import FilterTime from './FilterTime';

interface IConnectedProps {
    metaFacets: ITableReducer[TableNameType]['metaFacets'];
    searchResult: string[];
    searchValue: string;
    selectedFilters: ITableReducer[TableNameType]['filter'];
    selectOptions?: ITableReducer[TableNameType]['filterOptions'];
    sort: ITableReducer[TableNameType]['sort'];
    tableName: TableNameType;
    suggestIsLoading: boolean;
}

interface IDispatch {
    sendFieldSuggest: (payload: ISendFieldSuggestPayload) => void;
    sendTableSearch: (payload: ISendTableSearchPayload) => void;
    setTableFilter: (payload: ISetTableFilterPayload) => void;
    setTableOrder: (payload: ISetTableSortPayload) => void;
}

export type InnerFilterType<F extends ColumnFieldType, T extends ColumnTranslationType>
    = IColumn<F, T> & IDispatch & IConnectedProps;

export const InnerFilter =
    <Field extends ColumnFieldType, Translation extends ColumnTranslationType>
    (props: InnerFilterType<Field, Translation>) => {

        const {
            cellType,
            filterType,
            name,
            tableName,
            translation,
            searchResult,
            searchValue,
            selectedFilters,
            selectOptions,
        } = props;

        const selectFilter = get(selectedFilters, name);
        const options = get(selectOptions, name);

        const onChange = (value: IOnFilterChangeParams) => {
            if (props.isDisableFilter) return;

            if (isFilterEmpty(value)) {
                props.setTableFilter({ filter: omit(selectedFilters, name) });
            } else {
                props.setTableFilter({
                    currentFilter: { [name]: value },
                    filter: { ...selectedFilters, [name]: value },
                });
            }
        };

        const onSearch = (value: string): void => {
            props.sendTableSearch({
                field: props.name,
                search: value,
                tableName: tableName,
            });
        };
        switch (filterType) {
            case EColumnFilter.filterRange:
                return (
                    <FilterRange
                        filterValue={get(selectFilter, 'filterValue')}
                        isCurrency={cellType === EColumnCell.balance || cellType === EColumnCell.score}
                        translation={translation}
                        valueFrom={get(selectFilter, 'valueFrom')}
                        valueTo={get(selectFilter, 'valueTo')}
                        onChange={onChange}
                    />
                );
            case EColumnFilter.filterSelect:
                return (
                    <FilterSelect
                        selectedOption={selectFilter}
                        selectOptions={options}
                        translation={translation}
                        onChange={onChange}
                    />
                );
            case EColumnFilter.filterMultiSelect:
                return (
                    <FilterMultiSelect
                        field={name as unknown as keyof ColumnFieldType}
                        metaFacets={props.metaFacets}
                        selectedOptions={selectFilter}
                        selectOptions={options}
                        sendFieldSuggest={props.sendFieldSuggest}
                        suggestIsLoading={props.suggestIsLoading}
                        translation={translation}
                        onChange={onChange}
                    />
                );
            case EColumnFilter.filterLabelMultiSelect:
                return (
                    <FilterMultiSelect
                        field={name as unknown as keyof ColumnFieldType}
                        metaFacets={props.metaFacets}
                        renderOptions={
                            (props: IMultiSelectLabelProps & IMultiSelectItem) => <LabelSelectList {...props}/>
                        }
                        selectedOptions={selectFilter}
                        selectOptions={options}
                        suggestIsLoading={props.suggestIsLoading}
                        translation={translation}
                        onChange={onChange}
                    />
                );
            case EColumnFilter.filterDate:
                const tablesWithoutTime = [TableNameType.cashbackHistory];
                return (
                    <FilterDate
                        endDate={get(selectFilter, 'endDate')}
                        filterValue={get(selectFilter, 'filterValue')}
                        hideTimePicker={tablesWithoutTime.includes(tableName)}
                        startDate={get(selectFilter, 'startDate')}
                        translation={translation}
                        onChange={onChange}
                    />
                );
            case EColumnFilter.filterTime:
                return (
                    <FilterTime
                        commonTranslation={translation}
                        endTime={get(selectFilter, 'endDate')}
                        filterValue={get(selectFilter, 'filterValue')}
                        startTime={get(selectFilter, 'startDate')}
                        onChange={onChange}
                    />
                );
            case EColumnFilter.filterSearchInput:
                return (
                    <FilterSearchInput
                        searchResult={searchResult}
                        searchValue={searchValue}
                        tableName={props.tableName}
                        translation={translation}
                        value={selectFilter}
                        onChange={onChange}
                        onSearch={onSearch}
                    />
                );
            case EColumnFilter.filterSearch:
                return (
                    <FilterSearchInput
                        searchResult={searchResult}
                        searchValue={searchValue}
                        tableName={props.tableName}
                        translation={translation}
                        value={selectFilter}
                        onChange={onChange}
                    />
                );
            default:
                return <FilterStubStyled/>;
        }
    };

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    metaFacets: makeSelectorTableMetaFacets(),
    searchResult: makeSelectorTableFilterSearchResult(),
    searchValue: makeSelectorTableFilterSearchValue(),
    selectedFilters: makeSelectorTableFilter(),
    selectOptions: makeSelectorTableFilterOptions(),
    sort: makeSelectorTableSort(),
    tableName: makeSelectorTableCurrentName(),
    suggestIsLoading: makeSelectorTableSuggestIsLoading(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatch => ({
    sendFieldSuggest: (payload: ISendFieldSuggestPayload) => dispatch(sendFieldSuggest(payload)),
    sendTableSearch: (payload: ISendTableSearchPayload) => dispatch(sendTableSearch(payload)),
    setTableFilter: (payload: ISetTableFilterPayload) => dispatch(setTableFilter(payload)),
    setTableOrder: (payload: ISetTableSortPayload) => dispatch(setTableSortAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InnerFilter);
